import type { TaskType } from 'api-validations/ag-concepts'

import type { Stream } from '../../../operation-wide-data'
import type { Cff } from './catalog'

/**
 * What the user was looking at, expressed as the things a candidate stream can
 * share with it. Every field is optional: entering the map from a field card
 * has nothing to prefer, and falls through to recency alone.
 */
export type StreamPreference = {
  season?: number
  cropKey?: string
  task?: TaskType
  /** Keeps the user on the same machine's pass when the target has several. */
  serialNumber?: number
}

/** The parts of a stream that ranking reads — nothing here needs a whole `Stream`. */
export type RankableStream = Pick<Stream, 'season' | 'cropKey' | 'task' | 'serialNumber'>

/**
 * Most significant first. A candidate matching a field here beats any candidate
 * that does not, whatever the rest of its fields do — so this list is where
 * "does the user keep their crop or their task?" is answered.
 */
const PRECEDENCE = ['season', 'cropKey', 'task', 'serialNumber'] as const

/**
 * Which of `prefer`'s fields a candidate satisfies, in {@link PRECEDENCE}
 * order. An unset preference field never matches, so it neither favors nor
 * penalizes any candidate.
 */
const matchProfile = ({
  candidate,
  prefer
}: {
  candidate: RankableStream
  prefer: StreamPreference
}): boolean[] =>
  PRECEDENCE.map((field) => prefer[field] !== undefined && candidate[field] === prefer[field])

/**
 * Whether `candidate` is a closer match to `prefer` than `than` is. The most
 * significant field where exactly one of them matches decides the whole
 * comparison; everything below it is irrelevant, however much of it agrees.
 */
export const isCloserMatch = ({
  candidate,
  than,
  prefer
}: {
  candidate: RankableStream
  than: RankableStream
  prefer: StreamPreference
}): boolean => {
  const candidateMatches = matchProfile({ candidate, prefer })
  const otherMatches = matchProfile({ candidate: than, prefer })
  const deciding = candidateMatches.findIndex((matched, i) => matched !== otherMatches[i])
  return deciding !== -1 && candidateMatches[deciding]
}

/**
 * The best candidate for `prefer`, breaking ties on recency so equally-good
 * matches resolve to the most recent pass. `null` for an empty set.
 *
 * `recencyOf` is injected because the accurate answer costs a query; see
 * {@link lastModifiedRecency} for the free approximation.
 */
export const pickStream = <T extends RankableStream>({
  candidates,
  prefer,
  recencyOf
}: {
  candidates: readonly T[]
  prefer: StreamPreference
  recencyOf: (stream: NoInfer<T>) => number
}): T | null =>
  candidates.reduce<T | null>((best, candidate) => {
    if (best === null) return candidate
    if (isCloserMatch({ candidate, than: best, prefer })) return candidate
    if (isCloserMatch({ candidate: best, than: candidate, prefer })) return best
    return recencyOf(candidate) > recencyOf(best) ? candidate : best
  }, null)

/** Default recency for callers without stream overviews loaded. */
export const lastModifiedRecency = (stream: Pick<Stream, 'lastModified'>): number =>
  stream.lastModified.getTime()

export const streamsInField = <T extends Cff>(streams: readonly T[], cff: Cff): T[] =>
  streams.filter((s) => s.client === cff.client && s.farm === cff.farm && s.field === cff.field)

export const cffOf = (stream: Cff): Cff => ({
  client: stream.client,
  farm: stream.farm,
  field: stream.field
})
